import {
    Create,
    EditButton,
    ImageField,
    List,
    ReferenceField,
    Show,
    SimpleShowLayout,
    TextField,
    Pagination,
    required,
    BooleanField,
    BooleanInput,
    DateField,
    DateInput,
    ReferenceArrayField,
    ReferenceArrayInput,
    ArrayField,
    SingleFieldList,
    ChipField,
    SelectInput,
    DateTimeInput,
    fetchRelatedRecords,
    FormDataConsumer,
    AutocompleteInput,
    AutocompleteArrayInput,
    DatagridConfigurable,
    TopToolbar,
    SelectColumnsButton,
    FilterButton,
    CreateButton,
    ExportButton,
    WrapperField,
    ArrayInput,
    SimpleFormIterator,
    SelectField,
    useRecordContext,
} from "react-admin";
import { Edit, ReferenceInput, SimpleForm, TextInput } from "react-admin";
import {
    DurationField,
    AudioPlayerField,
    MediaUploadField,
    SaveAndAddAnotherToolbar,
    LongDateField,
    ShortDateField,
    TimeMSInput,
    TrackReferenceInput,
    ShareLink,
    ConfirmOnRecordButton,
    SearchSortButton,
} from "./customFields";
import { dataProvider } from "./dataProvider";
import { downloadCSV } from "react-admin";
import jsonExport from "jsonexport/dist";
import { Analytics } from "./analytics";
import { captureError } from "./sentry";
import { ArtistReferenceInput } from "./artists";
import { LabelReferenceInput } from "./labels";
import { DEFAULT_IS_PUBLIC } from "./config";
import { TagCreatePopup } from "./tags";
import { OrderedSingleFieldList } from "./orderedSingleFieldList";
import { BulkDeleteButton, BulkExportButton, BulkUpdateButton } from "react-admin";
import { Visibility, VisibilityOff, Audiotrack, Home, Lock, FileDownload } from "@mui/icons-material";
import { BulkUpdateForm } from "./bulkUpdate";
import { AlbumReferenceInput } from "./albums";
import { DistributorCreatePopup } from "./distributors";

const lockedTypes = [
    { id: "timed", name: "Until release date" },
    { id: "forever", name: "Forever" },
];

const lockedTypesWithFree = [{ id: "free", name: "Free" }, ...lockedTypes];

const trackTypes = [
    { id: "track", name: "Track" },
    { id: "dj_set", name: "DJ Set" },
];

const trackFilters = [
    <TextInput source="q" label="Search" alwaysOn />,
    <BooleanInput source="is_public" label="Public" defaultValue={true} />,
    <ArtistReferenceInput source="artist_id" label="Artist" create={false} />,
    <LabelReferenceInput source="label_id" label="Label" />,
    <AlbumReferenceInput source="album_id" label="Album" />,
    <ReferenceArrayInput source="genre_ids" label="Genre" reference="genres" />,
    <BooleanInput source="is_ddex_upload" label="Has been uploaded with DDEX" defaultValue={true} />,
    <BooleanInput source="is_ddex_available" label="Is DDEX deal active" defaultValue={true} />,
    <BooleanInput source="has_public_genre" label="Has public genre" defaultValue={true} />,
    <BooleanInput source="is_available" label="Is available" defaultValue={true} />,
    <SelectInput
        choices={lockedTypesWithFree}
        validate={[required()]}
        source="lock_type"
        label="Lock type"
        defaultValue="free"
    />,
    <SelectInput
        choices={trackTypes}
        validate={[required()]}
        source="type"
        label="Track type"
        defaultValue="track"
    />,
    <ReferenceInput source="distributor_id" reference="distributors" />,
    <BooleanInput source="missing_distributor" label="Missing distributor" defaultValue={true} />,
    <BooleanInput source="missing_album" label="Missing album" defaultValue={true} />,

    <BooleanInput source="is_premiere" label="Is premiere (exclusive or timed)" defaultValue={true} />,
    <BooleanInput source="is_download_available" label="Is download available" defaultValue={true} />,
    <TextInput source="isrc" label="ISRC" />,
    <BooleanInput source="has_manual_quality" defaultValue={true} />,
];

const supportedStreamingPrices = [
    { id: "0", name: "Free" },
    { id: "0.99", name: "$0.99" },
    { id: "1.29", name: "$1.29" },
    { id: "1.49", name: "$1.49" },
    { id: "1.99", name: "$1.99" },
    { id: "2.99", name: "$2.99" },
    { id: "4.99", name: "$4.99" },
    { id: "7.99", name: "$7.99" },
    { id: "9.99", name: "$9.99" },
    { id: "14.99", name: "$14.99" },
    { id: "19.99", name: "$19.99" },
];
const defaultPrice = "0";

const downloadPriceTypes = [
    { id: "unavailable", name: "Unavailable" },
    { id: "standard", name: "$1.49" },
    { id: "high", name: "$1.99" },
    { id: "free", name: "Free" },
];

const manualQualityChoices = [
    { id: 0.0, name: "0% (Very bad)" },
    { id: 0.1, name: "10%" },
    { id: 0.2, name: "20%" },
    { id: 0.3, name: "30%" },
    { id: 0.4, name: "40%" },
    { id: 0.5, name: "50%" },
    { id: 0.55, name: "55%" },
    { id: 0.6, name: "60%" },
    { id: 0.65, name: "65%" },
    { id: 0.7, name: "70%" },
    { id: 0.75, name: "75%" },
    { id: 0.8, name: "80%" },
    { id: 0.85, name: "85%" },
    { id: 0.9, name: "90%" },
    { id: 0.95, name: "95%" },
    { id: 1, name: "100% (Best possible quality)" },
];

const exporter = async (tracks: any) => {
    let artists = await fetchRelatedRecords(dataProvider)(tracks, "artist_ids", "artists");
    let genres = await fetchRelatedRecords(dataProvider)(tracks, "genre_ids", "genres");
    let labels = await fetchRelatedRecords(dataProvider)(tracks, "label_id", "labels");

    const data = tracks.map((track: any) => {
        let artist_names = track.artist_ids.map((artist_id: string) => artists[artist_id].name);

        return {
            id: track.id,
            name: track.name,
            artist_names: artist_names.join(", "),
            label_name: track.label_id ? labels[track.label_id].name : null,
            genres: track.genre_ids.map((genre_id: number) => genres[genre_id].name),
            is_public: track.is_public,
            audio_url: track.audio_url,
            image_url: track.image_url,
            release_date: track.release_date,
        };
    });

    jsonExport(
        data,
        {
            headers: [
                "id",
                "name",
                "artist_names",
                "label_name",
                "genres",
                "is_public",
                "audio_url",
                "image_url",
                "release_date",
            ],
        },
        (_: any, csv: any) => {
            downloadCSV(csv, "tracks");
        }
    );
};

const TrackActions = () => (
    <TopToolbar>
        <SearchSortButton />
        <SelectColumnsButton />
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

const TrackBulkActionButtons = () => (
    <>
        <BulkUpdateButton
            label="Make public"
            data={{ is_public: true }}
            icon={<Visibility />}
            mutationMode="pessimistic"
        />
        <BulkUpdateButton
            label="Make private"
            data={{ is_public: false }}
            icon={<VisibilityOff />}
            mutationMode="pessimistic"
        />
        <BulkUpdateForm label="Update label" icon={<Audiotrack />}>
            <LabelReferenceInput />
        </BulkUpdateForm>
        <BulkUpdateForm label="Update distributor" icon={<Home />}>
            <ReferenceInput source="distributor_id" reference="distributors">
                <AutocompleteInput fullWidth />
            </ReferenceInput>
        </BulkUpdateForm>
        <BulkUpdateForm label="Update streaming price" icon={<Lock />}>
            <TrackStreamingPriceAndLockSelector defaultValue={false} />
        </BulkUpdateForm>
        <BulkUpdateForm label="Update download price" icon={<FileDownload />}>
            <TrackDownloadPriceSelector />
        </BulkUpdateForm>
        <BulkUpdateButton
            label="Detach from DDEX"
            data={{ ddex_detached: true }}
            icon={<Visibility />}
            mutationMode="pessimistic"
        />
        <BulkExportButton />
        <BulkDeleteButton mutationMode="pessimistic" />
    </>
);

export const TrackList = () => (
    <List
        exporter={exporter}
        filters={trackFilters}
        perPage={50}
        pagination={<Pagination rowsPerPageOptions={[25, 50, 100, 200]} />}
        sort={{ field: "created_at", order: "DESC" }}
        actions={<TrackActions />}
    >
        <DatagridConfigurable
            rowClick="show"
            bulkActionButtons={<TrackBulkActionButtons />}
            omit={[
                "genres",
                "is_ddex_available",
                "is_available",
                "label_id",
                "release_date",
                "distributor_id",
                "type",
                "viberate_ranking",
            ]}
        >
            <TextField source="name" />
            <ImageField source="thumbnail_url" title="Track cover" label="Image" sortable={false} />
            <ReferenceArrayField label="Artists" reference="artists" source="artist_ids" sortable={false}>
                <OrderedSingleFieldList source="artist_ids">
                    <ChipField source="name" size="small" />
                </OrderedSingleFieldList>
            </ReferenceArrayField>
            <ReferenceArrayField
                label="Remixers"
                reference="artists"
                source="remixer_artist_ids"
                sortable={false}
            >
                <OrderedSingleFieldList source="remixer_artist_ids">
                    <ChipField source="name" size="small" />
                </OrderedSingleFieldList>
            </ReferenceArrayField>
            <ReferenceArrayField label="Genres" reference="genres" source="genre_ids" sortable={false} />
            <ReferenceField source="label_id" reference="labels" label="Label" link="show" />
            <ReferenceField
                source="distributor_id"
                reference="distributors"
                label="Distributor"
                link="show"
            />
            <TextField source="stream_count" label="Streams" />
            <TextField source="viberate_ranking" />
            <BooleanField source="is_public" label="Public" />
            <BooleanField source="is_ddex_available" label="DDEX Available" />
            <BooleanField source="is_available" label="Available" />
            <SelectField source="type" choices={trackTypes} label="Type" sortable={false} />
            <ShortDateField source="created_at" label="Added on" />
            <ShortDateField source="release_date" label="Release date" />
            <WrapperField label="Edit">
                <EditButton />
            </WrapperField>
        </DatagridConfigurable>
    </List>
);

const TrackMessages = () => {
    const record = useRecordContext();

    if (!record || !record.messages || record.messages.length === 0) {
        return null;
    }

    return (
        <div
            style={{
                marginBottom: "20px",
                backgroundColor: "#f8f8f8",
                padding: "5px 20px",
                borderRadius: "4px",
                borderLeft: "4px solid #cf0000",
            }}
        >
            <ul
                style={{
                    padding: "0 0 0 20px",
                    margin: 0,
                }}
            >
                {record.messages.map((message: any, index: any) => (
                    <li key={index} style={{ margin: "8px 0" }}>
                        <b>{message}</b>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export const TrackShow = () => (
    <Show>
        <SimpleShowLayout>
            <h2>Track Details</h2>
            <TextField source="name" label="Track Name" />
            <ArrayField source="status" label="Processing Status">
                <SingleFieldList linkType={false}>
                    <ChipField source="name" size="small" />
                </SingleFieldList>
            </ArrayField>
            <ReferenceArrayField label="Artists" reference="artists" source="artist_ids">
                <OrderedSingleFieldList source="artist_ids">
                    <ChipField source="name" size="small" />
                </OrderedSingleFieldList>
            </ReferenceArrayField>
            <ReferenceArrayField label="Remixers" reference="artists" source="remixer_artist_ids">
                <OrderedSingleFieldList source="remixer_artist_ids">
                    <ChipField source="name" size="small" />
                </OrderedSingleFieldList>
            </ReferenceArrayField>
            <ImageField source="image_url" title="Track cover" label="Artwork" />
            <ReferenceField source="label_id" reference="labels" label="Label" link="show" />
            <ReferenceField
                source="distributor_id"
                reference="distributors"
                label="Distributor"
                link="show"
            />
            <ReferenceArrayField label="Genres" reference="genres" source="genre_ids" />
            <TextField source="stream_count" label="Stream count" />
            <BooleanField source="is_public" />
            <BooleanField source="is_ddex_available" label="Is DDEX deal active" />
            <BooleanField source="is_available" />
            <TextField source="unlock_price_usd" label="Unlock price usd" />
            <SelectField source="download_price" choices={downloadPriceTypes} label="Download price" />
            <ShortDateField source="download_start_date" label="Download start date" />
            <ShortDateField source="download_end_date" label="Download end date" />
            <LongDateField source="unlock_date" label="Unlocked on" />
            <LongDateField source="created_at" label="Added on" />
            <DateField source="release_date" label="Release date" />
            <ReferenceField
                source="ddex_message_id"
                reference="ddex/messages"
                label="DDEX message"
                link="show"
            />
            <ShortDateField source="ddex_validity_start_time" />
            <ShortDateField source="ddex_validity_end_time" />
            <AudioPlayerField />
            <ConfirmOnRecordButton
                confirmTitle="Reencode this track?"
                confirmBody="Are you sure you want to reencode this track?"
                url="/cms/tracks/{id}/reencode"
                text="Reencode"
            />

            <DurationField source="duration_ms" label="Duration" />
            <ReferenceArrayField source="album_ids" reference="albums" label="Albums" />
            <TextField source="bpm" label="BPM" />
            <TextField source="key" label="Key" />
            <SelectField label="Type" choices={trackTypes} source="type" />
            <TextField source="isrc" label="ISRC" />
            <TextField source="gr_id" label="GRid" />
            <TextField source="manual_quality" />
            <TextField source="viberate_ranking" />
            <TextField source="spotify_streams" />
            <TextField source="soundcloud_streams" />
            <TextField source="shazams" />
            <TextField source="id" />
            <ShareLink type="track" />
            <Analytics resource="tracks" />
        </SimpleShowLayout>
    </Show>
);

const validateTrack = (values: any) => {
    try {
        const errors: any = {};
        console.log(values);
        if (values.genre_ids && values.genre_ids.length === 0) {
            errors.genre_ids = "Missing genres";
        }
        if (values.name && values.name.length > 100) {
            errors.name = `Name too big (${values.name.length}): 100 chars maximum`;
        }
        if (values.unlock_price_usd !== "0" && values.lock_type == null) {
            errors.lock_type = `Missing lock type`;
        }
        if (
            !values.artist_ids ||
            values.artist_ids.length == 0 ||
            !values.artist_ids.every((element: string) => element !== "")
        ) {
            errors.artist_ids = "Missing artists";
        }
        if (!values.distributor_id) {
            errors.distributor_id = "Missing distributor";
        }
        return errors;
    } catch (error) {
        captureError(error);
        throw error;
    }
};

export const TrackEdit = () => {
    return (
        <Edit mutationMode="pessimistic">
            <SimpleForm validate={validateTrack}>
                <h2>Edit Track</h2>

                <TrackDetailsEditor />

                <h2>Media Upload</h2>

                <MediaUploadField mediaType="image" requiresValidation={false} />
                <MediaUploadField mediaType="audio" requiresValidation={true} />
            </SimpleForm>
        </Edit>
    );
};

export const TrackCreate = () => (
    <Create>
        <SimpleForm validate={validateTrack} toolbar={<SaveAndAddAnotherToolbar />}>
            <h2>Add Track</h2>
            <TrackMessages />

            <TrackDetailsEditor />

            <h2>Media Upload</h2>

            <MediaUploadField mediaType="image" requiresValidation={true} />
            <MediaUploadField mediaType="audio" requiresValidation={true} />
        </SimpleForm>
    </Create>
);

type Track = {
    is_public: boolean;
    lock_type: string | null;
    unlock_date: string | null;
    unlock_price_usd: string | null;
    is_download_available: boolean;
};

const TrackUnlockSelector = (prop: { formData: Track }) => {
    if (prop.formData.unlock_price_usd !== "0" && prop.formData.unlock_price_usd != null) {
        return (
            <>
                <SelectInput
                    source="lock_type"
                    choices={lockedTypes}
                    validate={[required()]}
                    label="Lock type"
                />
                {prop.formData.lock_type === "timed" && (
                    <DateTimeInput source="unlock_date" label="Release date" validate={[required()]} />
                )}
            </>
        );
    } else {
        return <></>;
    }
};

const TrackSegmentsEditor = () => {
    return (
        <ArrayInput source="segments">
            <SimpleFormIterator>
                <FormDataConsumer>
                    {({ getSource }) => (
                        <div
                            style={{
                                width: "800px",
                                display: "flex",
                                flexDirection: "row",
                                gap: "15px",
                            }}
                        >
                            <TextInput source={getSource("name")} label="Segment Name" />
                            <TimeMSInput source={getSource("start_ms")} label="Start time" />
                            <TrackReferenceInput source={getSource("track_id")} label="Track" />
                        </div>
                    )}
                </FormDataConsumer>
            </SimpleFormIterator>
        </ArrayInput>
    );
};

const TrackStreamingPriceAndLockSelector = (props: { defaultValue: boolean }) => {
    return (
        <FormDataConsumer<Track>>
            {({ formData }) => (
                <div style={{ display: "flex", gap: "10px" }}>
                    <SelectInput
                        source="unlock_price_usd"
                        choices={supportedStreamingPrices}
                        defaultValue={props.defaultValue ? defaultPrice : null}
                        validate={[required()]}
                        label="Streaming price"
                    />
                    <TrackUnlockSelector formData={formData} />
                </div>
            )}
        </FormDataConsumer>
    );
};

const TrackDownloadPriceSelector = () => {
    return (
        <SelectInput
            source="download_price"
            choices={downloadPriceTypes}
            defaultValue={"unavailable"}
            validate={[required()]}
            label="Download price"
        />
    );
};

export const TrackDetailsEditor = () => {
    return (
        <div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ flex: 2, maxWidth: "500px" }}>
                    <TextInput
                        autoComplete="off"
                        validate={[required()]}
                        fullWidth
                        source="name"
                        label="Track Name"
                    />
                    <ArrayInput source="artist_ids" fullWidth label="Artists">
                        <SimpleFormIterator inline>
                            <ArtistReferenceInput />
                        </SimpleFormIterator>
                    </ArrayInput>
                    <ArrayInput source="remixer_artist_ids" fullWidth label="Remixers">
                        <SimpleFormIterator inline>
                            <ArtistReferenceInput />
                        </SimpleFormIterator>
                    </ArrayInput>
                    <TrackStreamingPriceAndLockSelector defaultValue={true} />
                    <TrackDownloadPriceSelector />
                    <BooleanInput source="is_public" defaultValue={DEFAULT_IS_PUBLIC} />
                </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", flexDirection: "column" }}>
                <div style={{ flex: 2, maxWidth: "500px" }}>
                    <ReferenceArrayInput label="Genres" source="genre_ids" reference="genres" />

                    <LabelReferenceInput />
                    <ReferenceInput source="distributor_id" reference="distributors">
                        <AutocompleteInput label="Distributor" create={<DistributorCreatePopup />} />
                    </ReferenceInput>

                    <DateInput source="release_date" label="Release date" />

                    <TextInput source="isrc" label="ISRC" fullWidth />
                    <TextInput source="gr_id" label="GRid" fullWidth />
                    <SelectInput choices={manualQualityChoices} source="manual_quality" fullWidth />

                    <SelectInput
                        source="type"
                        choices={trackTypes}
                        label="Type"
                        validate={[required()]}
                        defaultValue="track"
                    />

                    <FormDataConsumer>
                        {({ formData }) => formData.type == "dj_set" && <TrackSegmentsEditor />}
                    </FormDataConsumer>
                </div>
            </div>
        </div>
    );
};
